import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { toast } from "sonner";
import { MapPin, Waves, Wind, Wifi, Utensils, Car, Dumbbell, Gamepad2, Trophy, Flame, Baby, Flag, Anchor, ArrowRight, ShoppingCart, Bird, Wine, Landmark, Fish, Stethoscope } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import MeetFlorencia from "@/components/MeetFlorencia";
import BookingWidget from "@/components/BookingWidget";
import Lightbox from "@/components/Lightbox";
import { MEDIA, GUIDE, RESORT } from "@/lib/media";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useSeo } from "@/lib/seo";

const EASE = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, ease: EASE, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const MaskLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={{ y: "110%" }} animate={{ y: 0 }}
      transition={{ duration: 1, ease: EASE, delay }}
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} className="relative h-screen overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={MEDIA.hero} alt="Punto Nima resort ocean view" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/15" />
      </motion.div>
      <div className="relative z-10 h-full flex flex-col justify-end pb-14 sm:pb-20 lg:pb-28 px-6 lg:px-12 max-w-[1400px] mx-auto">
        <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
          className="overline text-white/80 mb-4 sm:mb-6 flex items-center gap-3">
          <MapPin className="w-4 h-4" strokeWidth={1.5} /> {t("San José del Cabo · Baja California Sur")}
        </motion.span>
        <h1 className="font-display font-light text-white text-[2.65rem] sm:text-7xl lg:text-[6rem] leading-[1.03] sm:leading-[0.92] tracking-tight max-w-5xl [text-shadow:0_2px_24px_rgba(0,0,0,0.4)]">
          <MaskLine delay={0.25}>{t("Your sanctuary above")}</MaskLine>
          <MaskLine delay={0.4}>{t("the")} <span className="italic">{t("Sea of Cortez")}</span>.</MaskLine>
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }}
          className="text-white font-normal text-base sm:text-lg lg:text-xl max-w-xl mt-5 sm:mt-8 leading-relaxed [text-shadow:0_1px_16px_rgba(0,0,0,0.55)]">
          {t("Punto Nima — a private coastal retreat where the days are golden, the water endless, and every moment is yours. Come stay a while.")}
        </motion.p>
        <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.9 }}
          data-testid="hero-cta" onClick={() => document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" })}
          className="mt-8 sm:mt-10 self-start px-8 py-3.5 sm:px-9 sm:py-4 bg-[#F6F4F0] text-[#1A1918] tracking-wide rounded-sm hover:bg-white transition-colors">
          {t("Check Availability")}
        </motion.button>
      </div>
    </section>
  );
}

function Manifesto({ property }) {
  const { t } = useLang();
  const chapters = [
    ["01", "The Residence", t("Light-filled, two-bedroom two-bath sanctuary styled in warm coastal neutrals — sleeps up to {n}, never crowded.").replace("{n}", property?.max_guests ?? 4)],
    ["02", "The Resort", t("Punto Nima's infinity pools, manicured gardens and beach access, moments from Punto Nima's private terrace.")],
    ["03", "The Setting", t("San José del Cabo — art walks, golden estuaries and world-class dining, all under the Baja sun.")],
  ];
  return (
    <section id="residence" className="px-6 lg:px-12 py-28 lg:py-40 max-w-[1400px] mx-auto">
      <Reveal className="max-w-3xl mb-8">
        <span className="overline text-[#B86B52]">{t("A love affair with the sea")}</span>
        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-6 text-[#1A1918]">
          {t("Wake to golden light, drift through unhurried days, and fall asleep to the whisper of the tide — an escape composed for the two of you, and the few lucky enough to join.")}
        </h2>
      </Reveal>
      <Reveal className="max-w-3xl mb-24">
        <span className="overline text-[#B86B52] block mb-3">{t("Fifth-floor views of the Baja")}</span>
        <p className="text-[#5C5A56] font-light text-lg leading-relaxed">
          {t("Perched on the fifth floor, Punto Nima rises above it all — with open, uninterrupted views across the Baja: the sparkling Sea of Cortez, the desert mountains and the resort's palm gardens below. Life up here means cooler sea breezes, greater privacy and quiet, and sunrises and sunsets that stretch clear to the horizon from your private terrace.")}
        </p>
      </Reveal>
      <Reveal className="mb-20 lg:mb-24">
        <div className="relative overflow-hidden rounded-sm group">
          <img src={MEDIA.poolCacti} alt="Rooftop infinity pool framed by desert cacti at Punto Nima"
            data-testid="love-affair-pool-image"
            className="w-full aspect-[21/9] object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
          <span className="absolute bottom-6 left-6 lg:bottom-8 lg:left-8 text-white/90 overline">{t("Rooftop infinity pool · Sea of Cortez")}</span>
        </div>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-x-12 gap-y-16">
        {chapters.map(([num, title, body], i) => (
          <Reveal key={num} delay={i * 0.1}>
            <div className="relative">
              <span className="font-display text-8xl font-light text-[#1A1918]/10 absolute -top-10 -left-2 select-none">{num}</span>
              <div className="relative pt-6">
                <h3 className="font-display text-3xl text-[#1A1918] mb-4">{t(title)}</h3>
                <p className="text-[#5C5A56] font-light leading-relaxed">{body}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Gallery() {
  const { t } = useLang();
  const items = [
    { src: MEDIA.living, span: "md:col-span-8", ratio: "aspect-[16/10]", label: "Living Room", caption: "Sink into sunlit calm, open to the terrace" },
    { src: MEDIA.greatRoom, span: "md:col-span-4", ratio: "aspect-[4/5]", label: "Great Room", caption: "Gather, unwind, linger a little longer" },
    { src: MEDIA.terraceLounge, span: "md:col-span-4", ratio: "aspect-[4/5]", label: "Terrace Lounge", caption: "Sundowners above the Sea of Cortez" },
    { src: MEDIA.terraceView, span: "md:col-span-8", ratio: "aspect-[16/10]", label: "Ocean View", caption: "Town and turquoise water from your balcony" },
    { src: MEDIA.livingWide, span: "md:col-span-7", ratio: "aspect-[16/10]", label: "Open Living", caption: "Light-filled, open-plan coastal living" },
    { src: MEDIA.breakfastBar, span: "md:col-span-5", ratio: "aspect-[16/10]", label: "Breakfast Bar", caption: "Morning coffee, ocean air" },
    { src: MEDIA.entry, span: "md:col-span-4", ratio: "aspect-[4/5]", label: "Entry Hallway", caption: "A gracious welcome home" },
    { src: MEDIA.dining, span: "md:col-span-8", ratio: "aspect-[16/10]", label: "Dining & Kitchen", caption: "Farm-to-table evenings, together" },
  ];
  const [lightbox, setLightbox] = useState(null);
  return (
    <section id="gallery" className="px-6 lg:px-12 py-20 lg:py-28 max-w-[1400px] mx-auto">
      <Reveal className="mb-16">
        <span className="overline text-[#B86B52]">{t("The Gallery")}</span>
        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 text-[#1A1918]">{t("Inside Punto Nima")}</h2>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 lg:gap-8">
        {items.map((it, i) => (
          <Reveal key={i} delay={(i % 2) * 0.08} className={it.span}>
            <figure>
              <button onClick={() => setLightbox(i)} data-testid={`gallery-item-${i}`}
                className="group relative overflow-hidden rounded-sm block w-full cursor-pointer">
                <img src={it.src} alt={it.label} className={`w-full ${it.ratio} object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105`} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="absolute bottom-4 left-5 text-white overline opacity-0 group-hover:opacity-100 transition-opacity duration-500">{t(it.label)} · {t("View")}</span>
              </button>
              <figcaption className="mt-3.5" data-testid={`gallery-caption-${i}`}>
                <span className="overline text-[#B86B52] text-[10px]">{t(it.label)}</span>
                <p className="font-display italic font-light text-[#5C5A56] text-base mt-1 leading-snug">{t(it.caption)}</p>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
      <Lightbox images={items} index={lightbox} onClose={() => setLightbox(null)} onIndex={setLightbox} />
    </section>
  );
}

function BedroomsBaths() {
  const { t } = useLang();
  const rooms = [
    { src: MEDIA.bedroomPrimary, span: "md:col-span-7", ratio: "aspect-[3/2]", num: "01", label: "Master Bedroom", note: "King bed · private terrace access · city & ocean view" },
    { src: MEDIA.bathPrimary, span: "md:col-span-5", ratio: "aspect-[3/2]", num: "02", label: "Primary Ensuite", note: "Double vanity · walk-in shower" },
    { src: MEDIA.bathShower, span: "md:col-span-5", ratio: "aspect-[3/2]", num: "03", label: "Guest Bathroom", note: "Travertine rainfall shower" },
    { src: MEDIA.bedroomGuest, span: "md:col-span-7", ratio: "aspect-[3/2]", num: "04", label: "Second Bedroom", note: "Queen bed · artisan Baja décor" },
  ];
  const [lightbox, setLightbox] = useState(null);
  return (
    <section id="rooms" className="px-6 lg:px-12 py-24 lg:py-32 max-w-[1400px] mx-auto">
      <Reveal className="mb-16 max-w-2xl">
        <span className="overline text-[#B86B52]">{t("Where You'll Sleep")}</span>
        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 text-[#1A1918]">{t("Two bedrooms, two baths")}</h2>
        <p className="text-[#5C5A56] font-light leading-relaxed mt-5">{t("Restful, light-filled rooms finished in warm woods and travertine — designed to sleep up to four in quiet comfort.")}</p>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 lg:gap-8">
        {rooms.map((r, i) => (
          <Reveal key={i} delay={(i % 2) * 0.08} className={r.span}>
            <button onClick={() => setLightbox(i)} data-testid={`room-item-${i}`}
              className="group relative overflow-hidden rounded-sm block w-full text-left cursor-pointer">
              <img src={r.src} alt={r.label} className={`w-full ${r.ratio} object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105`} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/5 to-transparent" />
              <div className="absolute bottom-5 left-6 right-6 flex items-end justify-between">
                <div>
                  <div className="text-white/70 font-display text-lg">{r.num}</div>
                  <div className="text-white font-display text-2xl lg:text-3xl leading-tight">{t(r.label)}</div>
                  <div className="text-white/80 text-sm font-light mt-1">{t(r.note)}</div>
                </div>
              </div>
            </button>
          </Reveal>
        ))}
      </div>
      <Lightbox images={rooms} index={lightbox} onClose={() => setLightbox(null)} onIndex={setLightbox} />
    </section>
  );
}

function LocalGuide() {
  const { t } = useLang();
  const [cats, setCats] = useState([]);
  const imgFor = { dining: GUIDE.dining, beach: GUIDE.beach, water: GUIDE.water };
  useEffect(() => { api.get("/guide").then((r) => setCats(r.data)).catch(() => {}); }, []);
  if (!cats.length) return null;
  return (
    <section id="guide" className="px-6 lg:px-12 py-28 lg:py-36 bg-[#EAE5DC]">
      <div className="max-w-[1400px] mx-auto">
        <Reveal className="mb-16 max-w-2xl mx-auto text-center">
          <span className="overline text-[#B86B52]">{t("The Local Guide")}</span>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 text-[#1A1918]">{t("Our favourite corners of Los Cabos")}</h2>
          <p className="text-[#5C5A56] font-light leading-relaxed mt-5">{t("A curated shortlist from the owner — the restaurants, beaches and experiences worth building your trip around.")}</p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-8">
          {cats.map((c, i) => (
            <Reveal key={c.key || i} delay={i * 0.1}>
              <div className="bg-white border border-[#1A1918]/10 rounded-sm overflow-hidden h-full flex flex-col">
                <div className="relative overflow-hidden">
                  <img src={imgFor[c.key] || GUIDE.beach} alt={c.title} className="w-full aspect-[4/3] object-cover" />
                  <span className="absolute top-4 left-4 overline text-white bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-sm">{t(c.tag)}</span>
                </div>
                <div className="p-7 flex-1">
                  <h3 className="font-display text-2xl text-[#1A1918] mb-5">{t(c.title)}</h3>
                  <ul className="space-y-4">
                    {c.items.map((it, j) => (
                      <li key={j} className="border-b border-[#1A1918]/10 pb-3 last:border-0">
                        <div className="text-[#1A1918] font-medium">{t(it.name)}</div>
                        {it.desc && <div className="text-[#5C5A56] text-sm font-light">{t(it.desc)}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function HouseRulesFAQ() {
  const { t } = useLang();
  const rules = [
    ["Check-in", "3:00 PM"], ["Check-out", "11:00 AM"], ["Parking", "1 secure on-site space"],
    ["Guests", "Up to 4 + baby"], ["Smoking", "No — outdoor terrace only"], ["Pets", "By request — ask first"],
  ];
  const faqs = [
    ["What are the check-in and check-out times?", "Check-in is from 3:00 PM and check-out is by 11:00 AM. Early check-in or late check-out can sometimes be arranged — just ask after booking."],
    ["Is parking available?", "Yes. Punto Nima includes one secure, gated on-site parking space within Punto Nima Resort. Street parking is also available nearby."],
    ["How do I get the keys / access?", "You'll receive detailed self check-in instructions and the door code by email a few days before arrival. Resort reception is staffed 24/7 if you need help."],
    ["How many guests can stay?", "Punto Nima comfortably sleeps up to 4 guests across its two bedrooms — ideal for couples, small families, or two travelling pairs."],
    ["Are pets allowed?", "Pets may be considered on a case-by-case basis — please contact the owner before booking so we can confirm. Unapproved pets incur a $250 fee."],
    ["What is the cancellation policy?", "Standard vacation-rental terms: cancel 30+ days before check-in for a full refund; cancel 14–30 days before for a 50% refund of nightly charges (cleaning fee always refunded before check-in); within 14 days, nightly charges are non-refundable. No-shows and early departures are charged as booked. Refunds return to your original payment method within 5–10 business days."],
    ["What are the house rules I'm agreeing to?", "By booking you agree to: check-in 3 PM / check-out 11 AM; maximum 4 registered guests; no parties or events; quiet hours 10 PM–8 AM; no smoking indoors (terrace only); and pets by prior approval only. The possession, use, manufacture, or distribution of any illegal drugs or controlled substances anywhere on the property is strictly prohibited and will result in immediate termination of the stay without refund and possible notification of the authorities. Guests must follow all Punto Nima resort rules and care for the home."],
    ["Is there a damage deposit, and how is it refunded?", "Yes. An $500 refundable damage deposit is collected at checkout when you pay through our secure Stripe checkout. After you check out and the home is inspected, the full $500 is refunded to the exact card/payment method you used — within 15 days of departure. If there is damage beyond normal wear, the repair/replacement cost is deducted from the deposit and the remainder is returned; costs exceeding the deposit may be billed separately."],
    ["How far is the airport?", "Los Cabos International Airport (SJD) is roughly a 20-minute drive. We're happy to recommend trusted airport transfer options."],
  ];
  return (
    <section id="faq" className="px-6 lg:px-12 py-28 lg:py-36 max-w-[1100px] mx-auto">
      <Reveal className="mb-14">
        <span className="overline text-[#B86B52]">{t("Good to Know")}</span>
        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 text-[#1A1918]">{t("House rules & FAQ")}</h2>
      </Reveal>
      <div className="grid lg:grid-cols-3 gap-6 mb-16">
        {rules.map(([k, v]) => (
          <Reveal key={k}>
            <div className="flex items-baseline justify-between border-b border-[#1A1918]/15 py-4">
              <span className="text-[#5C5A56] font-light">{t(k)}</span>
              <span className="text-[#1A1918] font-medium text-right">{t(v)}</span>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal>
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {faqs.map(([q, a], i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-[#1A1918]/15">
              <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left font-display text-xl lg:text-2xl text-[#1A1918] hover:no-underline py-6">{t(q)}</AccordionTrigger>
              <AccordionContent className="text-[#5C5A56] font-light text-base leading-relaxed pb-6">{t(a)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </section>
  );
}

function NearbyStrip() {
  const { t } = useLang();
  const cols = [
    { icon: Flag, tag: "On the Fairway", title: "Championship Golf", items: [
      ["Puerto Los Cabos Golf Club", "10 min · Norman & Nicklaus 27 holes"],
      ["Club Campestre San José", "12 min · Nicklaus signature"],
      ["Palmilla Golf Club", "15 min · 27 holes, ocean views"],
    ]},
    { icon: Anchor, tag: "By the Water", title: "World-Class Sportfishing", items: [
      ["Puerto Los Cabos Marina", "10 min · sportfishing fleet & charters"],
      ["Marlin & Dorado Charters", "Year-round · billfish capital of the world"],
      ["Gordo Banks", "Legendary offshore tuna & marlin grounds"],
    ]},
  ];
  return (
    <section id="nearby" className="px-6 lg:px-12 py-24 lg:py-32 bg-[#0F2537] text-[#F6F4F0]">
      <div className="max-w-[1400px] mx-auto">
        <Reveal className="mb-14 max-w-2xl">
          <span className="overline text-[#B86B52]">{t("Just Minutes Away")}</span>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5">{t("Golf & fishing")}</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-12">
          {cols.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.1}>
              <div className="flex items-center gap-3 mb-6">
                <c.icon className="w-5 h-5 text-[#B86B52]" strokeWidth={1.5} />
                <span className="font-display text-2xl lg:text-3xl">{t(c.title)}</span>
              </div>
              <ul>
                {c.items.map(([name, note]) => (
                  <li key={name} className="flex items-baseline justify-between gap-4 py-4 border-b border-white/10">
                    <span className="text-[#F6F4F0]">{t(name)}</span>
                    <span className="text-[#A3A09A] text-sm font-light text-right shrink-0">{t(note)}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function RooftopCinematic() {
  const { t } = useLang();
  return (
    <section className="relative h-[70vh] overflow-hidden" data-testid="rooftop-cinematic">
      <div className="absolute inset-0 animate-kenburns">
        <img src={RESORT.pool} alt="Punto Nima rooftop infinity pool" className="w-full h-full object-cover" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/25" />
      <div className="relative z-10 h-full flex flex-col justify-end pb-16 px-6 lg:px-12 max-w-[1400px] mx-auto">
        <Reveal>
          <span className="overline text-white/80 flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-[#B86B52] animate-pulse" /> {t("Live at the rooftop")}
          </span>
          <h2 className="font-display font-light text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mt-5">
            {t("The rooftop infinity pool")}
          </h2>
          <p className="text-white/85 font-light text-lg max-w-xl mt-5">{t("Sunrise laps over the Sea of Cortez, sunset cocktails at the bar — the crown of Punto Nima.")}</p>
        </Reveal>
      </div>
    </section>
  );
}

function ResortShowcase() {
  const { t } = useLang();
  const items = [
    { src: RESORT.exteriorDay, span: "md:col-span-8", ratio: "aspect-[16/10]", label: "The Building" },
    { src: RESORT.pool, span: "md:col-span-4", ratio: "aspect-[4/5]", label: "Rooftop Infinity Pool" },
    { src: RESORT.sunTerrace, span: "md:col-span-4", ratio: "aspect-[4/5]", label: "Rooftop Sun Terrace" },
    { src: RESORT.gameRoom, span: "md:col-span-8", ratio: "aspect-[16/10]", label: "Residents' Game Room & Bar" },
    { src: RESORT.courtyard, span: "md:col-span-6", ratio: "aspect-[3/2]", label: "Courtyard & Pickleball" },
    { src: RESORT.exteriorDusk, span: "md:col-span-6", ratio: "aspect-[3/2]", label: "Arrival at Dusk" },
  ];
  const [lightbox, setLightbox] = useState(null);
  return (
    <section id="resort" className="px-6 lg:px-12 py-28 lg:py-36 max-w-[1400px] mx-auto">
      <Reveal className="mb-14 max-w-2xl">
        <span className="overline text-[#B86B52]">{t("The Resort")}</span>
        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 text-[#1A1918]">{t("Life at Punto Nima")}</h2>
        <p className="text-[#5C5A56] font-light leading-relaxed mt-5">{t("Beyond your door: a rooftop infinity pool over the Sea of Cortez, sun terrace, residents' game room and bar, a landscaped courtyard with pickleball, secure elevator access and beach just steps away.")}</p>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 lg:gap-8">
        {items.map((it, i) => (
          <Reveal key={i} delay={(i % 2) * 0.08} className={it.span}>
            <button onClick={() => setLightbox(i)} data-testid={`resort-item-${i}`}
              className="group relative overflow-hidden rounded-sm block w-full cursor-pointer">
              <img src={it.src} alt={it.label} className={`w-full ${it.ratio} object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105`} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
              <span className="absolute bottom-4 left-5 text-white font-display text-xl lg:text-2xl">{t(it.label)}</span>
            </button>
          </Reveal>
        ))}
      </div>
      <p className="text-xs text-[#5C5A56] font-light mt-6">{t("Images: Punto Nima Resort architectural renderings.")}</p>
      <Lightbox images={items} index={lightbox} onClose={() => setLightbox(null)} onIndex={setLightbox} />
    </section>
  );
}

function MarqueeBar() {
  const { t } = useLang();
  const words = ["Ocean View", "Infinity Pool", "2 Bed · 2 Bath", "Private Terrace", "Beach Access", "Concierge"];
  return (
    <section className="bg-[#8C9B90]/25 py-8 border-y border-[#1A1918]/10 overflow-hidden">
      <Marquee speed={40} gradient={false}>
        {words.concat(words).map((w, i) => (
          <span key={i} className="font-display text-3xl lg:text-4xl text-[#2A4759] mx-16 font-light">{t(w)} <span className="text-[#B86B52] mx-8">✦</span></span>
        ))}
      </Marquee>
    </section>
  );
}

function Amenities() {
  const { t } = useLang();
  const list = [
    ["01", "Rooftop Infinity Pool", Waves], ["02", "Sea of Cortez Views", Waves],
    ["03", "Fitness Center / Gym", Dumbbell], ["04", "Residents' Game Room & Bar", Gamepad2],
    ["05", "Pickleball Court", Trophy], ["06", "BBQ & Grill Area", Flame],
    ["07", "Kids' Play Area", Baby], ["08", "Full Chef's Kitchen", Utensils],
    ["09", "High-Speed Wi-Fi", Wifi], ["10", "Central Air Conditioning", Wind],
    ["11", "Secure Elevator & Parking", Car], ["12", "Steps to the Beach", Waves],
    ["13", "Portable Crib Available", Baby],
  ];
  return (
    <section id="amenities" className="px-6 lg:px-12 py-28 lg:py-36 bg-[#EAE5DC]">
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
        <Reveal className="lg:sticky lg:top-32">
          <span className="overline text-[#B86B52]">{t("Amenities")}</span>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 text-[#1A1918] leading-[1.02]">
            {t("Comforts of a five-star hotel, intimacy of a private home.")}
          </h2>
          <p className="text-[#5C5A56] font-light leading-relaxed mt-6 max-w-md">
            {t("Every detail of Punto Nima is prepared for a seamless stay — from the linens to the local guidebook waiting on the counter.")}
          </p>
        </Reveal>
        <div>
          {list.map(([num, label, Icon], i) => (
            <Reveal key={num} delay={i * 0.05}>
              <div className="flex items-center gap-6 py-6 border-b border-[#1A1918]/10">
                <span className="font-display text-xl text-[#1A1918]/40 w-8">{num}</span>
                <Icon className="w-5 h-5 text-[#B86B52]" strokeWidth={1.5} />
                <span className="text-lg text-[#1A1918] font-light">{t(label)}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Location() {
  const { t } = useLang();
  return (
    <section id="location" className="relative">
      <div className="grid lg:grid-cols-2">
        <div className="relative h-[50vh] lg:h-auto overflow-hidden">
          <img src={MEDIA.terraceView} alt="San José del Cabo from Punto Nima terrace" className="w-full h-full object-cover" />
        </div>
        <div className="bg-[#0F2537] text-[#F6F4F0] px-6 lg:px-16 py-24 lg:py-36 flex flex-col justify-center">
          <Reveal>
            <span className="overline text-[#B86B52]">{t("The Location")}</span>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 leading-[1.02]">
              Punto Nima Resort,<br />San José del Cabo
            </h2>
            <p className="text-[#A3A09A] font-light leading-relaxed mt-6 max-w-md">
              {t("Nestled along the tranquil eastern cape of Los Cabos, Punto Nima blends golden-sand serenity with easy access to the marina, historic art district and Los Cabos International Airport — just 20 minutes away.")}
            </p>
            <div className="grid grid-cols-3 gap-8 mt-12">
              {[["20 min", "To Airport"], ["2 min", "To Beach"], ["24/7", "Concierge"]].map(([a, b]) => (
                <div key={b}>
                  <div className="font-display text-3xl lg:text-4xl">{a}</div>
                  <div className="text-[#A3A09A] text-sm mt-1 font-light">{t(b)}</div>
                </div>
              ))}
            </div>
            <div data-testid="location-map" className="mt-12 rounded-sm overflow-hidden border border-white/10">
              <iframe
                title="Punto Nima Resort location — Paseo Malecón San José 137, Zona Hotelera, San José del Cabo"
                src="https://maps.google.com/maps?q=Punto%20Nima%2C%20Paseo%20Malecon%20San%20Jose%20137%2C%20Zona%20Hotelera%2C%2023405%20San%20Jose%20del%20Cabo%2C%20BCS%2C%20Mexico&t=&z=16&ie=UTF8&iwloc=B&output=embed"
                width="100%" height="280" loading="lazy" style={{ border: 0, filter: "grayscale(0.2) contrast(1.05)" }}
                referrerPolicy="no-referrer-when-downgrade" />
            </div>
            <a href="https://www.google.com/maps/search/?api=1&query=Punto%20Nima%2C%20Paseo%20Malecon%20San%20Jose%20137%2C%20Zona%20Hotelera%2C%2023405%20San%20Jose%20del%20Cabo%2C%20BCS%2C%20Mexico"
              target="_blank" rel="noreferrer" data-testid="location-directions"
              className="inline-flex items-center gap-2 mt-4 text-sm text-[#B86B52] hover:text-[#d0836a] transition-colors">
              <MapPin className="w-4 h-4" strokeWidth={1.5} /> {t("View on Google Maps · steps from the Sea of Cortez")}
            </a>
            <div data-testid="walk-distances" className="mt-6">
              <span className="overline text-[#A3A09A] text-[10px]">{t("On foot from your door")}</span>
              <div className="grid grid-cols-2 gap-3 mt-3">
                {[
                  [Waves, "The Beach", "2 min walk"],
                  [ShoppingCart, "La Comer", "3 min walk"],
                  [Waves, "Playa Hotelera", "5 min walk"],
                  [Waves, "Costa Azul Beach", "10 min walk"],
                  [Bird, "San José Estuary & Bird Sanctuary", "10 min walk"],
                  [Utensils, "Cynthia's Restaurant", "1 min walk"],
                  [Wine, "Tap Deck SJC Restaurant Bar", "1 min walk"],
                  [Landmark, "Banorte ATM", "5 min walk"],
                  [Fish, "Claro Fish Jr", "2 min walk"],
                  [Stethoscope, "Longevity Medical Institute", "1 min walk", "1st floor"],
                  [ShoppingCart, "City Market", "8 min walk"],
                  [Wine, "Chambao Los Cabos", "6 min walk"],
                ].map(([Icon, place, time, note], idx) => (
                  <div key={idx} className="flex items-start gap-3 border border-white/10 rounded-sm px-4 py-3.5 bg-white/[0.03]">
                    <Icon className="w-5 h-5 text-[#B86B52] shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div>
                      <div className="text-[#F6F4F0] font-display text-base leading-tight">{t(place)}</div>
                      <div className="text-[#A3A09A] text-xs font-light mt-1">{t(time)}{note ? ` · ${t(note)}` : ""}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Policies() {
  const { t } = useLang();
  const cols = [
    {
      tag: "Cancellation", title: "Cancellation Policy",
      intro: "Standard vacation-rental terms, in line with Airbnb & VRBO:",
      items: [
        ["30+ days before check-in", "Full refund of all nightly charges and the cleaning fee."],
        ["14–30 days before check-in", "50% refund of nightly charges; cleaning fee fully refunded."],
        ["Within 14 days of check-in", "No refund of nightly charges; cleaning fee refunded if not yet incurred."],
        ["No-shows & early departures", "Charged as booked; no refund for unused nights."],
      ],
      note: "Refunds are processed to the original payment method within 5–10 business days. Dates are only held once payment is complete.",
    },
    {
      tag: "House Rules", title: "House Rules",
      intro: "By booking, guests agree to the following:",
      items: [
        ["Check-in 3:00 PM · Check-out 11:00 AM", "Late departures may incur an additional night."],
        ["Maximum 4 guests", "Only registered guests may stay overnight; no unregistered visitors."],
        ["No parties or events", "This is a quiet residential resort; quiet hours 10 PM–8 AM."],
        ["No smoking indoors", "Smoking permitted only on the open terrace."],
        ["Zero tolerance for illegal substances", "The possession, use, manufacture, or distribution of any illegal drug or controlled substance on the property is strictly prohibited. Any violation will result in immediate termination of the stay without refund and may be reported to the relevant authorities."],
        ["Pets by prior approval only", "Contact the owner before booking; unapproved pets incur a fee."],
        ["Care for the home & resort", "Follow all Punto Nima resort rules; lock up and conserve water/AC when out."],
      ],
    },
    {
      tag: "Damages & Security", title: "Damage Policy & Penalties",
      intro: "Guests are responsible for the condition of the residence:",
      items: [
        ["Refundable damage deposit", "An $500 refundable damage deposit is collected upfront at booking and returned in full within 15 days of checkout, provided there is no damage."],
        ["Accidental damage", "Report promptly. Guests are liable for repair/replacement of damage beyond normal wear, deducted from the deposit or billed if it exceeds the deposit."],
        ["Smoking violation", "$300 deep-cleaning & deodorizing fee."],
        ["Unapproved pet or extra guests", "$250 fee plus any related cleaning or damage costs."],
        ["Lost keys / fobs / excessive mess", "$150 replacement / additional cleaning fee as applicable."],
        ["Missing items or major damage", "Billed at full replacement cost beyond the deposit."],
      ],
    },
  ];
  return (
    <section id="policies" className="px-6 lg:px-12 py-24 lg:py-32 bg-[#EAE5DC]">
      <div className="max-w-[1400px] mx-auto">
        <Reveal className="mb-14 max-w-2xl">
          <span className="overline text-[#B86B52]">{t("Before You Book")}</span>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 text-[#1A1918]">{t("Important information for your stay")}</h2>
          <p className="text-[#5C5A56] font-light leading-relaxed mt-5">{t("By confirming your reservation, you accept our cancellation policy, house rules, and terms related to the deposit and any damages, detailed below.")}</p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-8">
          {cols.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div className="bg-white border border-[#1A1918]/10 rounded-sm p-8 h-full">
                <span className="overline text-[#B86B52]">{t(c.tag)}</span>
                <h3 className="font-display text-2xl text-[#1A1918] mt-2 mb-4">{t(c.title)}</h3>
                <p className="text-[#5C5A56] font-light text-sm mb-5">{t(c.intro)}</p>
                <ul className="space-y-4">
                  {c.items.map(([k, v]) => (
                    <li key={k} className="border-b border-[#1A1918]/10 pb-3 last:border-0">
                      <div className="text-[#1A1918] font-medium text-sm">{t(k)}</div>
                      <div className="text-[#5C5A56] text-sm font-light mt-0.5">{t(v)}</div>
                    </li>
                  ))}
                </ul>
                {c.note && <p className="text-xs text-[#5C5A56] font-light mt-5 italic">{t(c.note)}</p>}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Reviews() {
  const { t } = useLang();
  const [reviews, setReviews] = useState([]);
  useEffect(() => { api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {}); }, []);
  return (
    <section className="px-6 lg:px-12 py-28 lg:py-36 max-w-[1400px] mx-auto">
      <Reveal className="mb-16 max-w-2xl">
        <span className="overline text-[#B86B52]">{t("Guest Stories")}</span>
        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 text-[#1A1918]">{t("Loved by every guest")}</h2>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-8">
        {reviews.map((r, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div className="bg-white border border-[#1A1918]/10 rounded-sm p-8 h-full flex flex-col">
              <div className="text-[#B86B52] mb-5 tracking-widest">{"★".repeat(r.rating)}</div>
              <p className="font-display text-xl text-[#1A1918] leading-relaxed italic flex-1">"{r.text}"</p>
              <div className="mt-6 pt-5 border-t border-[#1A1918]/10">
                <div className="text-[#1A1918] font-medium">{r.name}</div>
                <div className="text-[#5C5A56] text-sm font-light">{r.location} · {r.date}</div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  useEffect(() => {
    const handler = () => {
      setForm((f) => ({
        ...f,
        message: t("I'm interested in an extended stay at Punto Nima (longer than three months).\n\nLength of stay: \nPreferred start date: \nNumber of guests: \n\nA little about our plans: "),
      }));
      setTimeout(() => {
        const el = document.getElementById("contact-message");
        el?.focus({ preventScroll: true });
        el?.setSelectionRange(el.value.length, el.value.length);
      }, 400);
    };
    window.addEventListener("prefill-extended-stay", handler);
    return () => window.removeEventListener("prefill-extended-stay", handler);
  }, [t]);
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", { ...form, lang });
      toast.success(t("Message sent — we'll be in touch shortly."));
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };
  const cls = "w-full bg-white border border-[#1A1918]/15 rounded-sm px-4 py-3 font-light focus:outline-none focus:border-[#B86B52] transition-colors";
  return (
    <section id="contact" className="px-6 lg:px-12 py-24 bg-[#EAE5DC]">
      <div className="max-w-[900px] mx-auto grid md:grid-cols-2 gap-12 items-center">
        <Reveal>
          <span className="overline text-[#B86B52]">{t("Have a question?")}</span>
          <h2 className="font-display font-light text-4xl lg:text-5xl tracking-tight mt-5 text-[#1A1918]">{t("Talk directly with your host")}</h2>
          <p className="text-[#5C5A56] font-light mt-5 leading-relaxed">{t("Have a special trip in mind, need specific dates, or want to know more about the property? Message me directly and I'll be glad to help you personally.")}</p>
        </Reveal>
        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="contact-form" className="space-y-4">
            <input required data-testid="contact-name" placeholder={t("Name")} className={cls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input required type="email" data-testid="contact-email" placeholder={t("Email")} className={cls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="contact-phone" placeholder={t("Phone (optional)")} className={cls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <textarea required rows={4} id="contact-message" data-testid="contact-message" placeholder={t("Your message")} className={`${cls} resize-none`} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            <button data-testid="contact-submit" disabled={sending} className="w-full py-4 bg-[#2A4759] text-white tracking-wide rounded-sm hover:bg-[#1e3644] transition-colors disabled:opacity-60">
              {sending ? t("Sending…") : t("Send Message")}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useLang();
  const [property, setProperty] = useState(null);
  useSeo({
    title: "Punto Nima · Luxury Condo in San José del Cabo · Derron Luxury",
    description: "Book Punto Nima directly — a light-filled two-bedroom condo with a rooftop infinity pool over the Sea of Cortez in San José del Cabo. Real-time availability and secure payment.",
    path: "/punto-nima",
  });
  useEffect(() => {
    api.get("/property").then((r) => setProperty(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <Nav />
      <Hero />
      <Manifesto property={property} />
      <MarqueeBar />
      <Gallery />
      <BedroomsBaths />
      <Amenities />
      <RooftopCinematic />
      <ResortShowcase />
      <Location />
      <NearbyStrip />
      <section id="booking" className="px-6 lg:px-12 py-28 lg:py-36 max-w-[1400px] mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-start [&>*]:min-w-0">
          <Reveal>
            <span className="overline text-[#B86B52]">{t("Reserve Your Stay")}</span>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 text-[#1A1918] leading-[1.02]">
              {t("Book Punto Nima directly")}
            </h2>            <p className="text-[#5C5A56] font-light mt-6 leading-relaxed max-w-md">
              {t("Real-time availability, transparent pricing, and secure card payment. No middleman, no platform surcharge — just your dates and the Baja coast.")}
            </p>
            <img src={MEDIA.terraceLounge} alt="Punto Nima terrace lounge" className="w-full aspect-[16/10] object-cover rounded-sm mt-10" />
            <div data-testid="extended-stay-note" className="mt-12 border-t border-[#1A1918]/10 pt-10 max-w-md">
              <span className="overline text-[#B86B52]">{t("Extended Escapes")}</span>
              <h3 className="font-display font-light text-3xl lg:text-4xl text-[#1A1918] leading-snug mt-4">
                {t("Staying beyond three months?")}
              </h3>
              <p className="text-[#5C5A56] font-light mt-5 leading-relaxed">
                {t("For seasons rather than stays, Florencia extends privately arranged rates reserved for long-term guests. Share your dates and desired length of stay, and she will compose an offer tailored to your extended escape.")}
              </p>
              <button data-testid="extended-stay-button"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent("prefill-extended-stay"));
                  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="group inline-flex items-center gap-2.5 mt-7 px-7 py-3.5 border border-[#2A4759] text-[#2A4759] text-sm tracking-wide rounded-sm hover:bg-[#2A4759] hover:text-white transition-colors">
                {t("Enquire about a longer stay")}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
              </button>
            </div>
          </Reveal>
          <div className="lg:sticky lg:top-28">
            <BookingWidget property={property} />
          </div>
        </div>
      </section>
      <Reviews />
      <Policies />
      <LocalGuide />
      <HouseRulesFAQ />
      <MeetFlorencia />
      <Contact />
      <Footer />
    </>
  );
}
